// receipt.js

import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import './receipt.css';
import OfficeReceiptPrint from './OfficeReceiptPrint';
import SchoolReceiptPrint from './SchoolReceiptPrint';
import { Link } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { db } from '../Firebase';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDoc,
  query,
  where,
  orderBy,
  setDoc
} from "firebase/firestore";


// --- Configuration for different receipt types ---
// FIELD MAPPING:
// 'รหัส' -> 'ID'
// 'เพศ' -> 'sex'
// 'ชื่อ' -> 'name'
// 'นามสกุล' -> 'surname'
// 'ชื่อเล่น' -> 'nickname'
// 'หมายเลขใบเสร็จ' -> 'office-ID'
// 'วันที่' -> 'date'
// 'Couse' -> 'couse'
// 'Level' -> 'course-level'
// 'วัน' -> 'day'
// 'เวลา' -> 'time'
// 'วันเริ่มคอร์ส' -> 'course-start'
// 'วันจบคอร์ส' -> 'course-end'
// 'รวม' -> 'course-price'
// 'หักส่วนลด' -> 'discount'
// 'รวมทั้งสิ้น' -> 'amount'
// 'จ่ายโดย' -> 'pay-by'
// 'complete' -> 'complete'
// 'เงินรับจริง' -> 'real-amount'
// 'หมายเหตุ' -> 'remark'
// 'หมายเหตุส่วนลด' -> 'discount-remark'

const OFFICE_CONFIG = {
  formFields: [
    { name: 'รหัส', dbKey: 'ID', type: 'text', readOnly: true, section: 'student' },
    { name: 'เพศ', dbKey: 'sex', type: 'select', optionsKey: 'sex', readOnly: true, section: 'student' },
    { name: 'ชื่อ', dbKey: 'name', type: 'text', readOnly: true, section: 'student' },
    { name: 'นามสกุล', dbKey: 'surname', type: 'text', readOnly: true, section: 'student' },
    { name: 'ชื่อเล่น', dbKey: 'nickname', type: 'text', readOnly: true, section: 'student' },
    { name: 'หมายเลขใบเสร็จ', dbKey: 'office-ID', type: 'text', readOnly: true, section: 'registration' },
    { name: 'วันที่', dbKey: 'date', type: 'date', section: 'registration' },
    { name: 'Couse', label: 'Course', dbKey: 'couse', type: 'select', optionsKey: 'course', section: 'registration' },
    { name: 'Level', dbKey: 'course-level', type: 'select', optionsKey: 'level', section: 'registration' },
    { name: 'วัน', dbKey: 'day', type: 'select', optionsKey: 'day', section: 'registration' },
    { name: 'เวลา', dbKey: 'time', type: 'select', optionsKey: 'time', section: 'registration' },
    { name: 'วันเริ่มคอร์ส', dbKey: 'course-start', type: 'date', section: 'registration' },
    { name: 'วันจบคอร์ส', dbKey: 'course-end', type: 'select', optionsKey: 'endDate', section: 'registration' },
    { name: 'รวม', dbKey: 'course-price', type: 'number', section: 'registration' },
    { name: 'หักส่วนลด', dbKey: 'discount', type: 'number', section: 'registration' },
    { name: 'รวมทั้งสิ้น', dbKey: 'amount', type: 'number', readOnly: true, section: 'registration' },
    { name: 'จ่ายโดย', dbKey: 'pay-by', type: 'select', optionsKey: 'paidBy', section: 'registration' },
    { name: 'complete', label: 'Completed', dbKey: 'complete', type: 'select', optionsKey: 'completed', section: 'registration' },
    { name: 'เงินรับจริง', dbKey: 'real-amount', type: 'number', readOnly: true, section: 'registration' },
    { name: 'หมายเหตุ', dbKey: 'remark', type: 'textarea', className: 'full-width', section: 'registration' },
    { name: 'หมายเหตุส่วนลด', dbKey: 'discount-remark', type: 'textarea', className: 'full-width', section: 'registration' },
  ],
  tableColumns: ['หมายเลขใบเสร็จ', 'วันที่', 'ชื่อ-สกุล', 'ชื่อเล่น', 'Course', 'Level', 'วัน', 'เวลา', 'วันเริ่มคอร์ส', 'ราคาคอร์ส', 'ส่วนลด'],
  sortableColumns: ['หมายเลขใบเสร็จ', 'ชื่อ-สกุล', 'ชื่อเล่น', 'Course'],
  filterableFields: ['ชื่อ-สกุล', 'ชื่อเล่น'], // These will need special handling or mapping
  searchPlaceholder: 'ค้นหาจากชื่อ-สกุล หรือ ชื่อเล่น...'
};
const SCHOOL_CONFIG = {
  formFields: [
    { name: 'เลขที่', dbKey: 'school-ID', type: 'text', readOnly: true },
    { name: 'วันที่', dbKey: 'date', type: 'date' },
    { name: 'โรงเรียน', dbKey: 'customer', type: 'select', optionsKey: 'schoolNames' },
    { name: 'ประเภท', dbKey: 'description', type: 'select', optionsKey: 'schoolDescription' },
    { name: 'หลักสูตร', dbKey: 'course', type: 'select', optionsKey: 'schoolCourses' },
    { name: 'ภาคเรียน', dbKey: 'term', type: 'text' },
    { name: 'งวดที่', dbKey: 'Period', type: 'text' },
    { name: 'จำนวนเงิน', dbKey: 'amount', type: 'number' },
    { name: 'วิธีการชำระ', dbKey: 'Payment-method', type: 'select', optionsKey: 'schoolPaymentMethods' },
    { name: 'ธนาคาร', dbKey: 'bank', type: 'select', optionsKey: 'schoolBanks' },
    { name: 'หมายเหตุ', dbKey: 'remark', type: 'textarea', className: 'full-width' },
  ],
  tableColumns: ['เลขที่', 'วันที่', 'โรงเรียน', 'ประเภท', 'หลักสูตร', 'งวดที่', 'จำนวนเงิน', 'วิธีการชำระ'],
  sortableColumns: ['เลขที่', 'วันที่', 'โรงเรียน', 'หลักสูตร'],
  filterableFields: ['โรงเรียน', 'หลักสูตร'],
  searchPlaceholder: 'ค้นหาจากโรงเรียน หรือ หลักสูตร...'
};
const parseDateToISO = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return '';
  const thaiMonths = {'ม.ค.':'01','ก.พ.':'02','มี.ค.':'03','เม.ย.':'04','พ.ค.':'05','มิ.ย.':'06','ก.ค.':'07','ส.ค.':'08','ก.ย.':'09','ต.ค.':'10','พ.ย.':'11','ธ.ค.':'12'};
  if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) return dateString.split('T')[0];
  const slashParts = dateString.split('/');
  if (slashParts.length === 3) {try {const day = slashParts[0].padStart(2, '0'); const month = slashParts[1].padStart(2, '0'); let year = parseInt(slashParts[2], 10); if (year > 2500) year -= 543; return `${year}-${month}-${day}`;} catch (e){}}
  const spaceParts = dateString.split(' ');
  if (spaceParts.length === 3 && thaiMonths[spaceParts[1]]) {try {const day = spaceParts[0].padStart(2, '0'); const month = thaiMonths[spaceParts[1]]; let year = parseInt(spaceParts[2], 10); if (year < 100) year += 2000; return `${year}-${month}-${day}`;} catch (e){}}
  return dateString;
};
const formatDateForDisplay = (dateString) => {
    const isoDate = parseDateToISO(dateString);
    if (isoDate && /^\d{4}-\d{2}-\d{2}/.test(isoDate)) {
        const [year, month, day] = isoDate.split('-');
        const buddhistYear = parseInt(year, 10) + 543;
        return `${day}/${month}/${buddhistYear}`;
    }
    return dateString;
};
const PrintModal = ({ children, isOpen }) => {
  if (!isOpen) return null;
  let printContainer = document.getElementById('print-container');
  if (!printContainer) {
    printContainer = document.createElement('div');
    printContainer.id = 'print-container';
    printContainer.className = 'print-only';
    document.body.appendChild(printContainer);
  }
  return ReactDOM.createPortal(children, printContainer);
};

// --- Helper Functions to map data ---

const mapToFormData = (dbData, config) => {
    // Normalize keys in dbData (trim whitespace)
    const normalizedDbData = {};
    Object.keys(dbData).forEach(key => {
        normalizedDbData[key.trim()] = dbData[key];
        // Keep original key too just in case
        normalizedDbData[key] = dbData[key];
    });

    const formData = { ...normalizedDbData }; // Start with normalized data
    
    config.formFields.forEach(field => {
        const keysToCheck = [field.dbKey, field.name];
        // Add common variations/fallbacks
        if (field.dbKey === 'course') keysToCheck.push('Course', 'couse', 'Couse', 'Courses', 'courses');
        if (field.dbKey === 'Payment-method') keysToCheck.push('payment-method', 'PaymentMethod', 'paymentMethod', 'Payment Method', 'payment method');
        // Add variations with spaces explicitly if needed, but trimming above should handle it
        if (field.dbKey === 'couse') keysToCheck.push('Couse', 'course', 'Course'); // Office fallback
        if (field.dbKey === 'sex') keysToCheck.push('Sex', 'gender', 'Gender');
        
        let found = false;
        for (const key of keysToCheck) {
            if (key && normalizedDbData[key] !== undefined) {
                formData[field.name] = normalizedDbData[key];
                found = true;
                break;
            }
        }
        if (!found && normalizedDbData[field.name] !== undefined) {
             formData[field.name] = normalizedDbData[field.name];
        }

        // --- Value Check / Correction ---
        // Handle "sex" field variations (ด.ช vs ด.ช.)
        if (field.name === 'เพศ') {
             // Explicitly check for 'sex' key as requested by user
             let val = normalizedDbData['sex'] || formData[field.name];
             if (val) {
                 val = String(val).trim();
                 if (val === 'ด.ช') val = 'ด.ช.';
                 if (val === 'ด.ญ') val = 'ด.ญ.';
                 formData[field.name] = val;
             }
        }
    });

    // Add rowId if present
    if (dbData.rowId) formData.rowId = dbData.rowId;
    return formData;
};

const mapToDbData = (formData, config) => {
    const dbData = { ...formData }; // Start with all form data
    config.formFields.forEach(field => {
        const dbKey = field.dbKey || field.name;
        // Map Form key (name) to DB key
        if (formData[field.name] !== undefined) {
            dbData[dbKey] = formData[field.name];
            // Optionally remove the UI-only keys if we want clean DB
            if (dbKey !== field.name) {
                delete dbData[field.name];
            }
        }
    });
    return dbData;
};


function ReceiptApp() {

  const [configYears, setConfigYears] = useState([]);
  const [dataYears, setDataYears] = useState([]);
  // Merge configYears and dataYears for the dropdown
  const availableYears = useMemo(() => {
      const years = Array.from(new Set([...configYears, ...dataYears]));
      return years.sort((a, b) => b.localeCompare(a));
  }, [configYears, dataYears]);

  const [selectedYear, setSelectedYear] = useState('');

  const [receiptType, setReceiptType] = useState('office');
  const [data, setData] = useState([]);
  // ... (lists state remains same)
  const [lists, setLists] = useState({
     sex: ['ด.ช.', 'ด.ญ.', 'นาย', 'นางสาว'],
     course: ['Math', 'Science', 'English', 'Thai', 'Social', 'Art', 'Robot'], 
     level: ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'M1', 'M2', 'M3', 'M4', 'M5', 'M6'],
     day: ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
     time: ['09:00-10:00', '10:00-11:00', '11:00-12:00', '13:00-14:00', '14:00-15:00', '15:00-16:00'],
     endDate: ['1 Month', '2 Months', '3 Months'],
     paidBy: ['Cash', 'Transfer', 'Credit Card'],
     teacher: [], // Added for Add Course feature
     schoolNames: ['School A', 'School B'], 
     categories: ['Tuition', 'Books', 'Activities'],
     courses: ['Normal', 'Special', 'Summer'],
     paymentMethods: ['Cash', 'Transfer', 'Check'],
     banks: ['KBank', 'SCB', 'BBL', 'KTB']
  });

  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [filterQuery, setFilterQuery] = useState('');

  const [isStudentSearchModalOpen, setIsStudentSearchModalOpen] = useState(false);
  const [studentsList, setStudentsList] = useState([]);
  const [isStudentListLoading, setIsStudentListLoading] = useState(false);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [studentSortConfig, setStudentSortConfig] = useState({ key: null, direction: 'ascending' });

  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [receiptToPrint, setReceiptToPrint] = useState(null);

  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [transferCourses, setTransferCourses] = useState([]);
  const [isTransferLoading, setIsTransferLoading] = useState(false);
  const [transferringItem, setTransferringItem] = useState(null);

  const [isAddCourseModalOpen, setIsAddCourseModalOpen] = useState(false);
  const [addCourseFormData, setAddCourseFormData] = useState({
    'course-ID': '',
    course: '',
    'course-end': '',
    'course-type': 'class',
    periods: [] // Array of { day: '', time: '', teacher: '' }
  });

  const [isSchoolTimetableModalOpen, setIsSchoolTimetableModalOpen] = useState(false);
  const [schoolTimetableList, setSchoolTimetableList] = useState([]);
  const [isSchoolTimetableLoading, setIsSchoolTimetableLoading] = useState(false);

  const [isAddSchoolCourseModalOpen, setIsAddSchoolCourseModalOpen] = useState(false);
  const [addSchoolCourseFormData, setAddSchoolCourseFormData] = useState({
    id: null,
    'school-course-ID': '',
    course: '',
    'course-end': '',
    'course-start': '',
    periods: []
  });

  const config = receiptType === 'office' ? OFFICE_CONFIG : SCHOOL_CONFIG;

  useEffect(() => {
    // Set default selected year if available and not set
    if (!selectedYear && availableYears.length > 0) {
        setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  useEffect(() => {
    const fetchConfigAndCourses = async () => {
        try {
            // Fetch Years Configuration
            const querySnapshot = await getDocs(collection(db, "configurations"));
            const years = querySnapshot.docs.map(doc => doc.id);
            setConfigYears(years);

            // Fetch Course, Sex, Level, Day, Time, Course-End, Pay-By, and Completed List from Admin/setup
            const setupDocRef = doc(db, "Admin", "setup");
            const setupDocSnap = await getDoc(setupDocRef);
            if (setupDocSnap.exists()) {
                const setupData = setupDocSnap.data();
                setLists(prev => {
                    const newLists = { ...prev };
                    if (setupData['office-course'] && Array.isArray(setupData['office-course'])) {
                        newLists.course = setupData['office-course'];
                    }
                    if (setupData['office-sex'] && Array.isArray(setupData['office-sex'])) {
                        newLists.sex = setupData['office-sex'];
                    }
                    if (setupData['office-level'] && Array.isArray(setupData['office-level'])) {
                        newLists.level = setupData['office-level'];
                    }
                    if (setupData['office-day'] && Array.isArray(setupData['office-day'])) {
                        newLists.day = setupData['office-day'];
                    }
                    if (setupData['office-time'] && Array.isArray(setupData['office-time'])) {
                        newLists.time = setupData['office-time'];
                    }
                    if (setupData['office-course-end'] && Array.isArray(setupData['office-course-end'])) {
                        newLists.endDate = setupData['office-course-end'];
                    }
                    if (setupData['office-teacher'] && Array.isArray(setupData['office-teacher'])) {
                        newLists.teacher = setupData['office-teacher'];
                    }
                    if (setupData['office-payby'] && Array.isArray(setupData['office-payby'])) {
                        newLists.paidBy = setupData['office-payby'];
                    }
                    if (setupData['office-complete'] && Array.isArray(setupData['office-complete'])) {
                        newLists.completed = setupData['office-complete'];
                    }
                    if (setupData['school-customer'] && Array.isArray(setupData['school-customer'])) {
                        newLists.schoolNames = setupData['school-customer'];
                    }
                    if (setupData['school-description'] && Array.isArray(setupData['school-description'])) {
                        newLists.schoolDescription = setupData['school-description'];
                    }
                    if (setupData['school-course'] && Array.isArray(setupData['school-course'])) {
                        newLists.schoolCourses = setupData['school-course'];
                    }
                    if (setupData['school-payby'] && Array.isArray(setupData['school-payby'])) {
                       newLists.schoolPaymentMethods = setupData['school-payby'];
                    }
                    if (setupData['school-bank'] && Array.isArray(setupData['school-bank'])) {
                        newLists.schoolBanks = setupData['school-bank'];
                    }
                    return newLists;
                });
            }
        } catch (error) {
            console.error("Error fetching configurations or courses:", error);
        }
    };
    fetchConfigAndCourses();
  }, []);




  const fetchData = async (type) => {
    setLoading(true);
    setData([]);
    setFilterQuery('');
    const defaultSortKey = type === 'office' ? 'หมายเลขใบเสร็จ' : 'เลขที่';
    setSortConfig({ key: defaultSortKey, direction: 'descending' });
    
    try {
        const collectionName = type === 'office' ? 'office-recipt' : 'school-recipt';
        
        const querySnapshot = await getDocs(collection(db, collectionName));
        const fetchedData = querySnapshot.docs.map(doc => {
            const rawData = doc.data();
            // Map DB data to UI Form Data structure for consistency in display/edit
            const mappedData = mapToFormData(rawData, type === 'office' ? OFFICE_CONFIG : SCHOOL_CONFIG);
            return {
                ...mappedData,
                rowId: doc.id
            };
        });
        
        // Extract years from data
        const extractedYears = new Set();
        fetchedData.forEach(item => {
            const dateStr = item['วันที่'];
            if (dateStr) {
                const iso = parseDateToISO(dateStr);
                if (iso) {
                    const year = parseInt(iso.split('-')[0]);
                    if (year < 2400) { // Assume AD
                       extractedYears.add(String(year + 543));
                    } else { // Assume BE
                       extractedYears.add(String(year));
                    }
                }
            }
        });
        setDataYears(Array.from(extractedYears));
        
        setData(fetchedData);

    } catch (error) {
      console.error("Error fetching data:", error);
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด!', text: 'ไม่สามารถดึงข้อมูลใบเสร็จได้' });
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    setIsStudentListLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, "student-register"));
      const studentsData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          applicationId: data.ID || doc.id, // Use ID from data or doc ID as fallback
          firstName: data.name,
          lastName: data.surname,
          nickname: data.nickname,
          gender: data.sex,
          // Add other fields if necessary
        };
      });
      setStudentsList(studentsData);
    } catch (error) {
      console.error("Error fetching students:", error);
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด!', text: 'ไม่สามารถดึงข้อมูลนักเรียนได้' });
      setIsStudentSearchModalOpen(false);
    } finally {
      setIsStudentListLoading(false);
    }
  };

  useEffect(() => {
    fetchData(receiptType);
  }, [receiptType, selectedYear]); 


  useEffect(() => {
    if (receiptType === 'office') {
      const price = parseFloat(String(formData['รวม'] || '0').replace(/[^0-9.-]/g, '')) || 0;
      const discount = parseFloat(String(formData['หักส่วนลด'] || '0').replace(/[^0-9.-]/g, '')) || 0;
      const subtotal = price - discount;
      const paymentMethod = formData['จ่ายโดย'];
      let totalReceived = subtotal;
      if (paymentMethod === 'บัตรเครดิต') {
        totalReceived = Math.round(subtotal * (1 - 0.016) * 100) / 100;
      }
      const subtotalForDisplay = subtotal.toFixed(2);
      const totalReceivedForDisplay = totalReceived.toFixed(2);
      if (formData['รวมทั้งสิ้น'] !== subtotalForDisplay || formData['เงินรับจริง'] !== totalReceivedForDisplay) {
        setFormData(prev => ({
          ...prev,
          'รวมทั้งสิ้น': subtotalForDisplay,
          'เงินรับจริง': totalReceivedForDisplay
        }));
      }
    }
  }, [receiptType, formData['รวม'], formData['หักส่วนลด'], formData['จ่ายโดย']]);

  const filteredAndSortedData = useMemo(() => {
    let items = [...data];
    
    if (selectedYear && selectedYear !== 'All') {
         items = items.filter(item => {
             const dateStr = item['วันที่'];
             if (!dateStr) return false;
             const iso = parseDateToISO(dateStr);
             if (iso) {
                 const year = parseInt(iso.split('-')[0]);
                 return String(year + 543) === selectedYear || String(year) === selectedYear;
             }
             return true; 
         });
    }

    if (filterQuery) { 
        items = items.filter(item => config.filterableFields.some(field => {
            // For custom filtering on compound fields like 'ชื่อ-สกุล'
            if (field === 'ชื่อ-สกุล') {
                const fullName = `${item['ชื่อ'] || ''} ${item['นามสกุล'] || ''}`;
                return fullName.toLowerCase().includes(filterQuery.toLowerCase());
            }
            return String(item[field] || '').toLowerCase().includes(filterQuery.toLowerCase())
        })); 
    }
    if (sortConfig.key) {
        items.sort((a, b) => {
            let key = sortConfig.key;
            if (key === 'Course') key = 'Couse';
            if (key === 'Completed') key = 'complete';
            if (key === 'ชื่อ-สกุล') {
                 // Sort by full name
                 const valA = `${a['ชื่อ']||''} ${a['นามสกุล']||''}`;
                 const valB = `${b['ชื่อ']||''} ${b['นามสกุล']||''}`;
                 const comparison = valA.localeCompare(valB, 'th-TH');
                 return sortConfig.direction === 'ascending' ? comparison : -comparison;
            }
            
            const valA = a[key];
            const valB = b[key];
            if (valA === null || valA === undefined) return 1;
            if (valB === null || valB === undefined) return -1;
            const comparison = String(valA).localeCompare(String(valB), 'th-TH');
            return sortConfig.direction === 'ascending' ? comparison : -comparison;
        });
    }
    return items;
  }, [data, filterQuery, sortConfig, config, selectedYear]);

  const sortedAndFilteredStudents = useMemo(() => {
    let filtered = [...studentsList];
    if (studentSearchQuery) {
        const query = studentSearchQuery.toLowerCase();
        filtered = studentsList.filter(student =>
            String(student.applicationId || '').includes(query) ||
            String(student.firstName || '').toLowerCase().includes(query) ||
            String(student.lastName || '').toLowerCase().includes(query) ||
            String(student.nickname || '').toLowerCase().includes(query)
        );
    }
    if (studentSortConfig.key) {
        filtered.sort((a, b) => {
            let valA, valB;
            if (studentSortConfig.key === 'fullName') {
                valA = `${a.firstName || ''} ${a.lastName || ''}`.trim();
                valB = `${b.firstName || ''} ${b.lastName || ''}`.trim();
            } else {
                valA = a[studentSortConfig.key];
                valB = b[studentSortConfig.key];
            }
            if (valA === null || valA === undefined) return 1;
            if (valB === null || valB === undefined) return -1;
            const comparison = String(valA).localeCompare(String(valB), 'th-TH');
            return studentSortConfig.direction === 'ascending' ? comparison : -comparison;
        });
    }
    return filtered;
  }, [studentsList, studentSearchQuery, studentSortConfig]);

  const transferCourseKeys = useMemo(() => {
    if (!transferCourses || transferCourses.length === 0) return ['course-ID', 'course', 'course-end', 'day', 'teacher', 'time'];
    const keys = ['course-ID', 'course', 'course-end', 'day', 'teacher', 'time'];
    
    const otherKeys = new Set();
    transferCourses.forEach(course => {
      Object.keys(course).forEach(key => {
        if (!['id', 'course-ID', 'course', 'course-end', 'day', 'time', 'teacher', 'period'].includes(key) && !key.startsWith('day-') && !key.startsWith('teacher-') && !key.startsWith('time-')) {
          otherKeys.add(key);
        }
      });
    });
    return [...keys, ...Array.from(otherKeys)];
  }, [transferCourses]);

  const schoolTimetableKeys = useMemo(() => {
    if (!schoolTimetableList || schoolTimetableList.length === 0) return ['school-course-ID', 'course', 'level', 'course-start', 'course-end'];
    
    // We will extract generic keys that aren't the standard ones dynamically.
    const keys = ['school-course-ID', 'course', 'level', 'course-start', 'course-end'];
    const otherKeys = new Set();
    schoolTimetableList.forEach(course => {
      Object.keys(course).forEach(key => {
        if (!['id', 'school-course-ID', 'course', 'school-name', 'test', 'course-start', 'course-end', 'period', 'level'].includes(key) && !key.startsWith('day-') && !key.startsWith('teacher-') && !key.startsWith('time-') && !key.startsWith('level-')) {
          otherKeys.add(key);
        }
      });
    });
    return [...keys, ...Array.from(otherKeys)];
  }, [schoolTimetableList]);

  const handleOpenSchoolTimetable = async () => {
    setIsSchoolTimetableModalOpen(true);
    setIsSchoolTimetableLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, "school-course-ID"));
      const dataList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSchoolTimetableList(dataList);
    } catch (error) {
      console.error("Error fetching school-course-ID:", error);
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด!', text: 'ไม่สามารถดึงข้อมูลตารางสอนโรงเรียนได้' });
      setIsSchoolTimetableModalOpen(false);
    } finally {
      setIsSchoolTimetableLoading(false);
    }
  };

  const handleTypeChange = (e) => { setReceiptType(e.target.value); };
  const requestSort = (key) => { let direction = 'ascending'; if (sortConfig.key === key && sortConfig.direction === 'ascending') { direction = 'descending'; } setSortConfig({ key, direction }); };
  const handleInputChange = (e) => { const { name, value } = e.target; setFormData(prev => ({ ...prev, [name]: value })); };
  const requestStudentSort = (key) => {
    let direction = 'ascending';
    if (studentSortConfig.key === key && studentSortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setStudentSortConfig({ key, direction });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formDataToSend = { ...formData };
    config.formFields.forEach(field => {
      if (field.type === 'date' && formDataToSend[field.name]) {
        const isoDate = formDataToSend[field.name];
        if (isoDate && /^\d{4}-\d{2}-\d{2}/.test(isoDate)) {
          const [year, month, day] = isoDate.split('-');
          const buddhistYear = parseInt(year, 10) + 543;
          formDataToSend[field.name] = `${day}/${month}/${buddhistYear}`;
        }
      }
      if (field.type === 'number' && typeof formDataToSend[field.name] === 'string') {
          formDataToSend[field.name] = parseFloat(formDataToSend[field.name].replace(/[^0-9.-]/g, '')) || 0;
      }
    });

    // Map keys to DB Keys before sending
    const dbDataToSend = mapToDbData(formDataToSend, config);

    try {
      const collectionName = receiptType === 'office' ? 'office-recipt' : 'school-recipt';
      
      if (isEditing && formData.rowId) {
          // Update existing
          const docRef = doc(db, collectionName, formData.rowId);
          // Remove rowId from data payload
          const { rowId, ...updateData } = dbDataToSend;
          await updateDoc(docRef, updateData);
      } else {
          // Create new
          // Remove rowId if it accidentally got in there (it shouldn't for new doc)
          const { rowId, ...newData } = dbDataToSend;
          
          if (receiptType === 'office') {
              // Use 'office-ID' as the explicit Document ID for Office Receipts
              const documentId = String(newData['office-ID']);
              await setDoc(doc(db, collectionName, documentId), newData);
          } else if (receiptType === 'school') {
              // Use 'school-ID' as the explicit Document ID for School Receipts
              const documentId = String(newData['school-ID']);
              await setDoc(doc(db, collectionName, documentId), newData);
          } else {
              // Fallback just in case
              await addDoc(collection(db, collectionName), newData);
          }
      }

      setIsModalOpen(false);
      Swal.fire({ icon: 'success', title: 'สำเร็จ!', timer: 1500, showConfirmButton: false });
      fetchData(receiptType);
    } catch (error) {
      console.error("Error saving receipt:", error);
      Swal.fire({ icon: 'error', title: 'ผิดพลาด!', text: error.message || 'เกิดข้อผิดพลาด' });
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setIsEditing(false);
    if (receiptType === 'office') {
      setStudentSearchQuery('');
      setStudentSortConfig({ key: null, direction: 'ascending' });
      setIsStudentSearchModalOpen(true);
      fetchStudents();
    } else if (receiptType === 'school') {
      const existingIds = data.map(item => parseInt(item['เลขที่'], 10)).filter(id => !isNaN(id));
      const nextId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 105001;
      setFormData({ 'เลขที่': String(nextId), 'วันที่': new Date().toISOString().split('T')[0], 'จำนวนเงิน': 0, 'หมายเหตุ': '' });
      setIsModalOpen(true);
    }
  };

  const openEditModal = (item) => {
    setIsEditing(true);
    const formattedItem = { ...item };
    config.formFields.forEach(field => {
      const value = formattedItem[field.name];
      if (value !== null && value !== undefined) {
        if (field.type === 'date') {
          formattedItem[field.name] = parseDateToISO(value);
        }
        if (field.type === 'number' && typeof value === 'string') {
          formattedItem[field.name] = value.replace(/[^0-9.-]/g, '');
        }
      }
    });
    setFormData(formattedItem);
    setIsModalOpen(true);
  };

  const handleSelectStudent = (student) => {
        // Calculate Next Receipt ID
    // Note: We need to parse 'office-ID' or 'หมายเลขใบเสร็จ' which might be mapped
    // Since 'data' is already mapped to form keys, we can look at 'หมายเลขใบเสร็จ'
    const existingReceiptIds = data
      .map(item => parseInt(item['หมายเลขใบเสร็จ'], 10))
      .filter(id => !isNaN(id));

    const nextReceiptId = existingReceiptIds.length > 0
      ? Math.max(...existingReceiptIds) + 1
      : 421001;

    const newFormData = {
      'หมายเลขใบเสร็จ': String(nextReceiptId),
      'รหัส': student.applicationId,
      'เพศ': student.gender,
      'ชื่อ': student.firstName,
      'นามสกุล': student.lastName,
      'ชื่อเล่น': student.nickname,
      'วันที่': new Date().toISOString().split('T')[0],
      'รวม': 0,
      'หักส่วนลด': 0,
      'complete': 'No',
      'หมายเหตุ': '',
    };
    setFormData(newFormData);
    setIsStudentSearchModalOpen(false);
    setIsModalOpen(true);
  };

  const handleDelete = (rowId) => { Swal.fire({ title: 'คุณแน่ใจหรือไม่?', text: "ข้อมูลจะถูกลบอย่างถาวร!", icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'ใช่, ลบเลย!', cancelButtonText: 'ยกเลิก' }).then(async (result) => { if (result.isConfirmed) {
    setLoading(true);
    try {
      const collectionName = receiptType === 'office' ? 'office-recipt' : 'school-recipt';
      await deleteDoc(doc(db, collectionName, rowId));
      Swal.fire('ลบเรียบร้อย!', '', 'success');
      fetchData(receiptType);
    } catch (error) { 
        console.error("Error deleting:", error);
        Swal.fire('เกิดข้อผิดพลาด!', error.message || 'ไม่สามารถลบข้อมูลได้', 'error'); 
    } finally { setLoading(false); } } }); };

  const renderFormField = (field) => (
    <div key={field.name} className={`form-field ${field.className || ''}`}>
      <label>{field.label || (field.name === 'รวม' ? 'ราคา (รวม)' : field.name)}</label>
      {field.type === 'select' ? (
        <select name={field.name} value={formData[field.name] ?? ''} onChange={handleInputChange} disabled={field.readOnly}>
          <option value="">-- เลือก --</option>
          {(lists[field.optionsKey] || field.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      ) : field.type === 'textarea' ? (
        <textarea name={field.name} value={formData[field.name] ?? ''} onChange={handleInputChange} />
      ) : (
        <input type={field.type} name={field.name} value={formData[field.name] ?? ''} onChange={handleInputChange} readOnly={field.readOnly} />
      )}
    </div>
  );

  // Print Logic: might need to map keys back to what Print Component expects 
  // or update Print Component.
  // Assuming Print Component expects FORM KEYS (Thai keys), which we have in 'data' state.
  const handlePrint = (item) => {
    const formattedItem = { ...item };
    config.formFields.forEach(field => {
      if (field.type === 'date' && formattedItem[field.name]) {
        formattedItem[field.name] = parseDateToISO(formattedItem[field.name]);
      }
    });
    setReceiptToPrint(formattedItem);
    setIsPrintModalOpen(true);

    setTimeout(() => {
      window.print();
      setIsPrintModalOpen(false);
      setReceiptToPrint(null);
    }, 500);
  };

  const handleTransfer = async (item) => {
    setTransferringItem(item);
    setIsTransferLoading(true);
    
    if (receiptType === 'school') {
      setIsSchoolTimetableModalOpen(true);
      try {
        const querySnapshot = await getDocs(collection(db, "school-course-ID"));
        const dataList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        const itemCourse = String(item['หลักสูตร'] || item['course'] || '').trim();
        const filteredCourses = dataList.filter(course => 
          String(course.course || '').trim() === itemCourse
        );

        setSchoolTimetableList(filteredCourses.length > 0 ? filteredCourses : dataList);
      } catch (error) {
        console.error("Error fetching school-course-ID:", error);
        Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด!', text: 'ไม่สามารถดึงข้อมูลตารางสอนโรงเรียนได้' });
        setIsSchoolTimetableModalOpen(false);
      } finally {
        setIsTransferLoading(false);
      }
    } else {
      setIsTransferModalOpen(true);
      try {
        const querySnapshot = await getDocs(collection(db, "course-ID"));
        const coursesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        const filteredCourses = coursesData.filter(course => {
          const itemCourse = String(item['Couse'] || item['course'] || item['Course'] || '').trim();
          return String(course.course || '').trim() === itemCourse;
        });

        setTransferCourses(filteredCourses);
      } catch (error) {
        console.error("Error fetching course-ID:", error);
        Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด!', text: 'ไม่สามารถดึงข้อมูล Course-ID ได้' });
        setIsTransferModalOpen(false);
      } finally {
        setIsTransferLoading(false);
      }
    }
  };

  const openAddCourseModal = async () => {
    if (!transferringItem) return;

    setIsTransferLoading(true);
    try {
      const coursesRef = collection(db, "course-ID");
      const querySnapshot = await getDocs(coursesRef);
      let maxID = 0;
      querySnapshot.forEach((doc) => {
        const id = parseInt(doc.data()['course-ID'], 10);
        if (id > maxID) maxID = id;
      });
      const nextID = maxID + 1;

      const normalizeDay = (day) => day && typeof day === 'string' && day.startsWith('วัน') ? day.replace('วัน', '') : day;
      const initialDay = normalizeDay(transferringItem['วัน'] || '');
      const initialTime = transferringItem['เวลา'] || '';
      
      const initialPeriods = [];
      if (initialDay) {
        initialPeriods.push({
          day: initialDay,
          time: initialTime || lists.time[0] || '',
          teacher: lists.teacher[0] || ''
        });
      }

      setAddCourseFormData({
        id: null,
        'course-ID': nextID,
        course: transferringItem['Couse'] || transferringItem['course'] || transferringItem['Course'] || '',
        'course-end': transferringItem['วันจบคอร์ส'] || transferringItem['course-end'] || (lists.endDate[0] || ''),
        'course-type': 'class',
        periods: initialPeriods
      });
      setIsAddCourseModalOpen(true);
    } catch (error) {
      console.error("Error generating next course-ID:", error);
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด!', text: 'ไม่สามารถสร้างรหัสคอร์สใหม่ได้' });
    } finally {
      setIsTransferLoading(false);
    }
  };

  const openEditCourseModal = (course) => {
    const normalizeDay = (day) => day && typeof day === 'string' && day.startsWith('วัน') ? day.replace('วัน', '') : day;
    const parsedPeriods = [];
    if (course.period && typeof course.period === 'object') {
      if (Array.isArray(course.period)) {
        course.period.forEach(p => {
          if (p.day) {
            parsedPeriods.push({
              day: normalizeDay(p.day),
              time: p.time || lists.time[0] || '',
              teacher: p.teacher || lists.teacher[0] || ''
            });
          }
        });
      } else {
        // Parse object like { 'day-1': '...', 'teacher-1': '...' }
        let i = 1;
        while (course.period[`day-${i}`] !== undefined) {
          parsedPeriods.push({
            day: normalizeDay(course.period[`day-${i}`]),
            time: course.period[`time-${i}`] || lists.time[0] || '',
            teacher: course.period[`teacher-${i}`] || lists.teacher[0] || ''
          });
          i++;
        }
      }
    } else if (course.day) {
      // Fallback for flat structure
      parsedPeriods.push({
        day: normalizeDay(course.day),
        time: course.time || lists.time[0] || '',
        teacher: course.teacher || lists.teacher[0] || ''
      });
    }

    setAddCourseFormData({
      id: course.id,
      'course-ID': course['course-ID'],
      course: course.course || (transferringItem ? (transferringItem['Couse'] || transferringItem['course'] || transferringItem['Course']) : ''),
      'course-end': course['course-end'] || (lists.endDate[0] || ''),
      'course-type': course['course-type'] || 'class',
      periods: parsedPeriods
    });
    setIsAddCourseModalOpen(true);
  };

  const handleDayToggle = (day) => {
    setAddCourseFormData(prev => {
      const exists = prev.periods.find(p => p.day === day);
      if (exists) {
        return { ...prev, periods: prev.periods.filter(p => p.day !== day) };
      } else {
        return { ...prev, periods: [...prev.periods, { day, time: lists.time[0] || '', teacher: lists.teacher[0] || '' }] };
      }
    });
  };

  const handlePeriodChange = (day, field, value) => {
    setAddCourseFormData(prev => ({
      ...prev,
      periods: prev.periods.map(p => p.day === day ? { ...p, [field]: value } : p)
    }));
  };

  const handleAddCourseSubmit = async (e) => {
    e.preventDefault();
    if (addCourseFormData.periods.length === 0) {
      Swal.fire({ icon: 'warning', title: 'คำเตือน', text: 'กรุณาเลือกอย่างน้อย 1 วันเรียน' });
      return;
    }

    setLoading(true);
    try {
      const periodObj = {};
      addCourseFormData.periods.forEach((p, idx) => {
        periodObj[`day-${idx + 1}`] = p.day;
        periodObj[`time-${idx + 1}`] = p.time;
        periodObj[`teacher-${idx + 1}`] = p.teacher;
      });

      const courseDataToSave = {
        'course-ID': addCourseFormData['course-ID'],
        course: addCourseFormData.course,
        'course-end': addCourseFormData['course-end'],
        'course-type': addCourseFormData['course-type'] || 'class',
        period: periodObj
      };

      if (addCourseFormData.id) {
        // Update existing
        await updateDoc(doc(db, "course-ID", addCourseFormData.id), courseDataToSave);
        Swal.fire({ icon: 'success', title: 'สำเร็จ!', text: 'แก้ไขข้อมูลคอร์สเรียบร้อยแล้ว' });
      } else {
        // Create new
        const newDocId = String(addCourseFormData['course-ID']);
        await setDoc(doc(db, "course-ID", newDocId), courseDataToSave);
        Swal.fire({ icon: 'success', title: 'สำเร็จ!', text: 'เพิ่มข้อมูลคอร์สใหม่เรียบร้อยแล้ว' });
      }
      
      setIsAddCourseModalOpen(false);
      handleTransfer(transferringItem);
    } catch (error) {
      console.error("Error saving/updating course-ID:", error);
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด!', text: 'ไม่สามารถบันทึกข้อมูลได้' });
    } finally {
      setLoading(false);
    }
  };

  const openAddSchoolCourseModal = async () => {
    setIsSchoolTimetableLoading(true);
    try {
      const coursesRef = collection(db, "school-course-ID");
      const querySnapshot = await getDocs(coursesRef);
      let maxID = 0;
      querySnapshot.forEach((doc) => {
        const docData = doc.data();
        const id = parseInt(docData['school-course-ID'], 10);
        if (!isNaN(id) && id > maxID) {
          maxID = id;
        }
      });
      const nextID = maxID > 0 ? maxID + 1 : 100001; // Start at 100001 if empty

      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yy = String(now.getFullYear() + 543).substring(2); // Buddhist Era
      const today = `${dd}/${mm}/${yy}`;

      setAddSchoolCourseFormData({
        id: null,
        'school-course-ID': String(nextID),
        course: 'จารุ - บรรจุหลักสูตร',
        'course-end': '15 ครั้ง',
        'course-start': today,
        periods: []
      });
      setIsAddSchoolCourseModalOpen(true);
    } catch (error) {
      console.error("Error generating next school-course-ID:", error);
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด!', text: 'ไม่สามารถสร้าางรหัสคอร์สใหม่ได้' });
    } finally {
      setIsSchoolTimetableLoading(false);
    }
  };

  const handleSchoolDayToggle = (day) => {
    setAddSchoolCourseFormData(prev => {
      const exists = prev.periods.find(p => p.day === day);
      if (exists) {
        return { ...prev, periods: prev.periods.filter(p => p.day !== day) };
      } else {
        return { ...prev, periods: [...prev.periods, { day, time: '09.00-10.00', teacher: lists.teacher[0] || '', level: '' }] };
      }
    });
  };

  const handleSchoolPeriodChange = (day, field, value) => {
    setAddSchoolCourseFormData(prev => ({
      ...prev,
      periods: prev.periods.map(p => p.day === day ? { ...p, [field]: value } : p)
    }));
  };

  const handleAddSchoolCourseSubmit = async (e) => {
    e.preventDefault();
    if (addSchoolCourseFormData.periods.length === 0) {
      Swal.fire({ icon: 'warning', title: 'คำเตือน', text: 'กรุณาเลือกอย่างน้อย 1 วันเรียน' });
      return;
    }

    // Validation for course-start
    const courseStart = addSchoolCourseFormData['course-start'];
    const dateMatch = courseStart.match(/^(\d{2})\/(\d{2})\/(\d{2})$/);
    if (!dateMatch) {
      Swal.fire({ icon: 'error', title: 'รูปแบบวันที่ไม่ถูกต้อง', text: 'กรุณากรอกวันที่ในรูปแบบ วว/ดด/ปป (เช่น 12/04/69)' });
      return;
    }

    const d = parseInt(dateMatch[1], 10);
    const m = parseInt(dateMatch[2], 10);
    const y = parseInt(dateMatch[3], 10);

    if (y < 50) {
      Swal.fire({ icon: 'warning', title: 'ปี พ.ศ. ไม่ถูกต้อง', text: 'กรุณาใช้ปี พ.ศ. (เช่น 69)' });
      return;
    }

    if (m < 1 || m > 12) {
      Swal.fire({ icon: 'error', title: 'ข้อมูลไม่ถูกต้อง', text: 'ข้อมูล เดือน ไม่ถูกต้อง' });
      return;
    }

    const testDate = new Date(y + 2000 - 543, m - 1, d);
    if (testDate.getDate() !== d || testDate.getMonth() !== m - 1) {
      Swal.fire({ icon: 'error', title: 'ข้อมูลไม่ถูกต้อง', text: 'ข้อมูล วัน ไม่ถูกต้อง' });
      return;
    }

    setLoading(true);
    try {
      const periodObj = {};
      addSchoolCourseFormData.periods.forEach((p, idx) => {
        periodObj[`day-${idx + 1}`] = p.day;
        periodObj[`time-${idx + 1}`] = p.time;
        periodObj[`teacher-${idx + 1}`] = p.teacher;
        periodObj[`level-${idx + 1}`] = p.level || '';
      });

      const courseDataToSave = {
        'school-course-ID': addSchoolCourseFormData['school-course-ID'],
        course: addSchoolCourseFormData.course,
        'course-end': addSchoolCourseFormData['course-end'],
        'course-start': addSchoolCourseFormData['course-start'],
        period: periodObj
      };

      if (addSchoolCourseFormData.id) {
        // Update existing (Future proof)
        await updateDoc(doc(db, "school-course-ID", addSchoolCourseFormData.id), courseDataToSave);
        Swal.fire({ icon: 'success', title: 'สำเร็จ!', text: 'แก้ไขข้อมูลตารางสอนโรงเรียนเรียบร้อยแล้ว', timer: 1500, showConfirmButton: false });
      } else {
        // Create new
        const newDocId = String(addSchoolCourseFormData['school-course-ID']);
        await setDoc(doc(db, "school-course-ID", newDocId), courseDataToSave);
        Swal.fire({ icon: 'success', title: 'สำเร็จ!', text: 'เพิ่มข้อมูลตารางสอนโรงเรียนเรียบร้อยแล้ว', timer: 1500, showConfirmButton: false });
      }
      
      setIsAddSchoolCourseModalOpen(false);
      handleOpenSchoolTimetable(); // Refresh the parent modal list
    } catch (error) {
      console.error("Error saving/updating school-course-ID:", error);
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด!', text: 'ไม่สามารถบันทึกข้อมูลได้' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditSchoolCourse = (course) => {
    const transformedPeriods = [];
    if (course.period && typeof course.period === 'object') {
      if (Array.isArray(course.period)) {
        course.period.forEach(p => {
          transformedPeriods.push({
            day: p.day || '',
            time: p.time || '',
            teacher: p.teacher || '',
            level: p.level || ''
          });
        });
      } else {
        let i = 1;
        while (course.period[`day-${i}`] !== undefined || course.period[`teacher-${i}`] !== undefined || course.period[`time-${i}`] !== undefined || course.period[`level-${i}`] !== undefined) {
          transformedPeriods.push({
            day: course.period[`day-${i}`] || '',
            time: course.period[`time-${i}`] || '',
            teacher: course.period[`teacher-${i}`] || '',
            level: course.period[`level-${i}`] || ''
          });
          i++;
        }
      }
    }

    setAddSchoolCourseFormData({
      id: course.id,
      'school-course-ID': course['school-course-ID'] || '',
      course: course.course || '',
      'course-end': course['course-end'] || '15 ครั้ง',
      'course-start': course['course-start'] || '',
      periods: transformedPeriods
    });
    setIsAddSchoolCourseModalOpen(true);
  };

  const handleSelectTransferCourse = async (course) => {
    // 1. ตรวจสอบว่าเป็นคอร์สโรงเรียนหรือไม่
    const isActuallySchool = !!course['school-course-ID'];

    // --- CASE 1: SCHOOL TIMETABLE TRANSFER ---
    if (isActuallySchool) {
      const confirmResult = await Swal.fire({
        title: 'ยืนยันการสร้างตารางสอนโรงเรียน?',
        text: `ระบบจะสร้างข้อมูลลงใน school-timesheet สำหรับคอร์ส ${course['school-course-ID']}`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'ใช่, สร้างเลย!',
        cancelButtonText: 'ยกเลิก'
      });

      if (!confirmResult.isConfirmed) return;

      setIsTransferLoading(true);
      try {
        const courseBaseId = String(course['school-course-ID']).trim();
        
        // ดึงข้อมูล Period ทั้งหมด
        const periods = [];
        if (course.period) {
          if (Array.isArray(course.period)) {
            course.period.forEach((p, idx) => {
              if (p.day) periods.push({ ...p, originalIndex: idx + 1 });
            });
          } else if (typeof course.period === 'object') {
            let i = 1;
            while (course.period[`day-${i}`] !== undefined) {
              periods.push({
                day: course.period[`day-${i}`],
                time: course.period[`time-${i}`],
                teacher: course.period[`teacher-${i}`],
                level: course.period[`level-${i}`],
                originalIndex: i
              });
              i++;
            }
          }
        }

        if (periods.length === 0) {
          throw new Error("คอร์สนี้ไม่มีข้อมูล Period ที่สมบูรณ์");
        }

        // ค้นหา Suffix ล่าสุดที่มีในระบบ (-01, -02, ...)
        const schoolTimesheetRef = collection(db, "school-timesheet");
        const q = query(schoolTimesheetRef, 
          where("__name__", ">=", courseBaseId + "-"), 
          where("__name__", "<=", courseBaseId + "-\uf8ff")
        );
        const querySnapshot = await getDocs(q);
        
        const existingSuffixes = new Set();
        querySnapshot.docs.forEach(doc => {
          const parts = doc.id.split('-');
          const s = parseInt(parts[parts.length - 1]);
          if (!isNaN(s)) existingSuffixes.add(s);
        });
        
        let nextSuffixNum = 1;
        while (existingSuffixes.has(nextSuffixNum)) {
          nextSuffixNum++;
        }

        // สร้างเอกสารแยกตามแต่ละ Period
        for (const p of periods) {
          const suffixStr = String(nextSuffixNum).padStart(2, '0');
          const newDocId = `${courseBaseId}-${suffixStr}`;
          
          const timesheetData = {
            courseID: courseBaseId,
            day: p.day || '-',
            time: p.time || '-',
            teacher: p.teacher || '-',
            level: p.level || '-',
            'course-start': String(course['course-start'] || '-'),
            timesheet: {}
          };

          await setDoc(doc(db, "school-timesheet", newDocId), timesheetData);
          nextSuffixNum++;
        }

        Swal.fire({ icon: 'success', title: 'สำเร็จ!', text: `โอนย้ายข้อมูลเรียบร้อยแล้ว (${periods.length} รายการ)`, timer: 1500, showConfirmButton: false });
        setIsSchoolTimetableModalOpen(false);
        setTransferringItem(null);
        fetchData(receiptType);
      } catch (error) {
        console.error("Error creating school timesheet:", error);
        Swal.fire({ icon: 'error', title: 'ผิดพลาด!', text: error.message });
      } finally {
        setIsTransferLoading(false);
      }
      return;
    }

    // --- CASE 2: OFFICE RECEIPT TRANSFER (Original Logic) ---
    let selectedItem = transferringItem;

    if (!selectedItem) {
      Swal.fire({ 
        icon: 'warning', 
        title: 'ยังไม่ได้เลือกใบเสร็จ!', 
        text: 'กรุณาเลือกใบเสร็จที่ต้องการโอนย้ายก่อนครับ' 
      });
      return;
    }

    Swal.fire({
      title: 'ยืนยันการโอนย้ายข้อมูล?',
      text: `คุณกำลังจะโอนย้ายคอร์สไปยังใบเสร็จ: ${selectedItem['เลขที่'] || selectedItem['school-ID'] || selectedItem['office-ID']}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'ใช่, โอนย้ายเลย!',
      cancelButtonText: 'ยกเลิก'
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsTransferLoading(true);
        try {
          const isSchool = receiptType === 'school';
          const collectionName = isSchool ? "school-timesheet" : "time-sheet";
          const receiptCollection = isSchool ? "school-recipt" : "office-recipt";
          
          const receiptNo = isSchool 
            ? (selectedItem['เลขที่'] || selectedItem['school-ID'])
            : (selectedItem['หมายเลขใบเสร็จ'] || selectedItem['office-ID']);
            
          const docId = receiptNo ? String(receiptNo) : selectedItem.rowId;


          if (!docId || docId === "" || docId === "undefined") {
            throw new Error("ไม่สามารถระบุ ID ของใบเสร็จได้");
          }
          
          const timesheetData = isSchool ? {
            customer: String(selectedItem['โรงเรียน'] || selectedItem['customer'] || ''),
            reciptID: Number(selectedItem['เลขที่'] || selectedItem['school-ID']) || 0,
            courseID: Number(course['school-course-ID']) || 0,
            'course-start': String(course['course-start'] || ''),
            timesheet: {}
          } : {
            customerID: Number(selectedItem['รหัส'] || selectedItem['ID']) || 0,
            reciptID: Number(selectedItem['หมายเลขใบเสร็จ'] || selectedItem['office-ID']) || 0,
            courseID: Number(course['school-course-ID'] || course['course-ID']) || 0,
            'course-start': String(selectedItem['วันเริ่มคอร์ส'] || selectedItem['course-start'] || ''),
            timesheet: {}
          };

          await setDoc(doc(db, collectionName, docId), timesheetData);

          // อัปเดตสถานะใบเสร็จต้นทาง (เฉพาะฝั่ง Office เท่านั้น)
          if (!isSchool) {
            const receiptDocId = selectedItem.rowId || String(selectedItem['หมายเลขใบเสร็จ'] || selectedItem['office-ID'] || '');
            if (receiptDocId && receiptDocId !== "" && receiptDocId !== "undefined") {
              const receiptDocRef = doc(db, receiptCollection, String(receiptDocId));
              await setDoc(receiptDocRef, { complete: "Yes" }, { merge: true });
              fetchData(receiptType);
            }
          } else {
            fetchData(receiptType);
          }

          Swal.fire({ icon: 'success', title: 'สำเร็จ!', text: 'โอนย้ายข้อมูลเรียบร้อยแล้ว', timer: 1500, showConfirmButton: false });
          setIsTransferModalOpen(false);
          setIsSchoolTimetableModalOpen(false);
          setTransferringItem(null);
        } catch (error) {
          console.error("Error creating timesheet:", error);
          Swal.fire({ icon: 'error', title: 'ผิดพลาด!', text: 'ไม่สามารถสร้างข้อมูลได้: ' + error.message });
        } finally {
          setIsTransferLoading(false);
        }
      }
    });
  };

  return (
    <div className="ReceiptApp">
      {isStudentSearchModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content modal-lg">
            <button className="modal-close-btn" onClick={() => setIsStudentSearchModalOpen(false)}>&times;</button>
            <h3 className="modal-header-title">ค้นหาข้อมูลนักเรียน</h3>
            <div className="student-search-controls">
              <input type="text" placeholder="ค้นหาจากรหัส, ชื่อ, นามสกุล, หรือชื่อเล่น..." value={studentSearchQuery} onChange={(e) => setStudentSearchQuery(e.target.value)} className="filter-input" />
            </div>
            <div className="table-container student-search-results">
              {isStudentListLoading ? <div className="loading-indicator">กำลังโหลดข้อมูลนักเรียน...</div> : (
                <table>
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => requestStudentSort('applicationId')}>รหัส{studentSortConfig.key === 'applicationId' && <span className="sort-icon">{studentSortConfig.direction === 'ascending' ? ' ▲' : ' ▼'}</span>}</th>
                      <th className="sortable" onClick={() => requestStudentSort('fullName')}>ชื่อ-สกุล{studentSortConfig.key === 'fullName' && <span className="sort-icon">{studentSortConfig.direction === 'ascending' ? ' ▲' : ' ▼'}</span>}</th>
                      <th className="sortable" onClick={() => requestStudentSort('nickname')}>ชื่อเล่น{studentSortConfig.key === 'nickname' && <span className="sort-icon">{studentSortConfig.direction === 'ascending' ? ' ▲' : ' ▼'}</span>}</th>
                      <th>เลือก</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAndFilteredStudents.map((student) => (
                      <tr key={student.rowIndex}>
                        <td>{student.applicationId}</td>
                        <td>{`${student.firstName || ''} ${student.lastName || ''}`}</td>
                        <td>{student.nickname}</td>
                        <td><button className="btn-primary" onClick={() => handleSelectStudent(student)}>เลือก</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {isTransferModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content modal-lg">
            <button className="modal-close-btn" onClick={() => setIsTransferModalOpen(false)}>&times;</button>
            <h3 className="modal-header-title">ข้อมูลคอร์ส</h3>
            
            {transferringItem && (
              <div className="transfer-context-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', padding: '10px', backgroundColor: '#f0f4f8', borderRadius: '8px' }}>
                <div>
                  <p style={{ margin: '0 0 5px 0' }}><strong>ใบเสร็จ:</strong> {transferringItem['หมายเลขใบเสร็จ'] || transferringItem['office-ID']} | <strong>นักเรียน:</strong> {transferringItem['ชื่อ']} {transferringItem['นามสกุล']}</p>
                  <p style={{ margin: 0 }}>
                    <strong>Course:</strong> {transferringItem['Couse'] || transferringItem['course'] || transferringItem['Course'] || '-'} | 
                    <strong> Day:</strong> {transferringItem['วัน'] || '-'} | 
                    <strong> Time:</strong> {transferringItem['เวลา'] || '-'}
                  </p>
                </div>
                <div>
                  <button className="btn-primary" onClick={openAddCourseModal}>เพิ่มข้อมูล</button>
                </div>
              </div>
            )}

            <div className="table-container transfer-table-container">
              {isTransferLoading ? <div className="loading-indicator">กำลังโหลดข้อมูล...</div> : (
                <table>
                  <thead>
                    <tr>
                      {transferCourseKeys.length > 0 ? (
                        <>
                          {transferCourseKeys.map(key => <th key={key}>{key}</th>)}
                          <th style={{ textAlign: 'center' }}>จัดการ</th>
                        </>
                      ) : <th>ไม่มีข้อมูล</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {transferCourses.length > 0 ? (
                      transferCourses.map((course) => {
                        const rowData = { ...course };
                        return (
                          <tr key={course.id}>
                            {transferCourseKeys.map(key => {
                              if (key === 'day' || key === 'teacher' || key === 'time') {
                                // Extract and stack period values vertically
                                const stackedValues = [];
                                if (course.period && typeof course.period === 'object') {
                                  if (Array.isArray(course.period)) {
                                    course.period.forEach(p => {
                                      stackedValues.push(p[key] || '-');
                                    });
                                  } else {
                                    // Parse object like { 'day-1': '...', 'day-2': '...' }
                                    let i = 1;
                                    while (course.period[`day-${i}`] !== undefined || course.period[`teacher-${i}`] !== undefined || course.period[`time-${i}`] !== undefined) {
                                      stackedValues.push(course.period[`${key}-${i}`] || '-');
                                      i++;
                                    }
                                  }
                                } else {
                                  // Fallback to directly mapped 1-period (old format before parsing period arrays)
                                  stackedValues.push(course[key] || course[`${key}-1`] || '-');
                                }
                                
                                return (
                                  <td key={key}>
                                    {stackedValues.length > 0 ? stackedValues.map((val, idx) => (
                                      <div key={idx} style={{ padding: '2px 0', borderBottom: idx < stackedValues.length - 1 ? '1px solid #eee' : 'none', minHeight: '24px' }}>
                                        {val}
                                      </div>
                                    )) : '-'}
                                  </td>
                                );
                              }

                              // Standard rendering for other keys
                              const val = course[key];
                              return (
                                <td key={key}>{val !== undefined && val !== null && val !== '' ? String(val) : '-'}</td>
                              );
                            })}
                            <td style={{ textAlign: 'center', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                              <button className="icon-btn edit-btn" style={{ marginRight: '8px' }} onClick={() => openEditCourseModal(course)} title="แก้ไข">
                                <EditIcon />
                              </button>
                              <button className="icon-btn primary-btn" onClick={() => handleSelectTransferCourse(course)} title="เลือก">
                                <CheckCircleOutlineIcon />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr><td colSpan={transferCourseKeys.length + 1}>ไม่มีข้อมูลในระบบ</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {isAddCourseModalOpen && (
        <div className="modal-overlay" style={{ zIndex: 1100 }}>
          <div className="modal-content" style={{ width: '600px', maxWidth: '90%' }}>
            <button className="modal-close-btn" onClick={() => setIsAddCourseModalOpen(false)}>&times;</button>
            <h3 className="modal-header-title">เพิ่มข้อมูลคอร์ส</h3>
            <form onSubmit={handleAddCourseSubmit} className="modal-form-container">
              <div className="modal-form-body">
                <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                  <div className="form-field">
                    <label>course-ID</label>
                    <input type="text" value={addCourseFormData['course-ID']} readOnly />
                  </div>
                  <div className="form-field">
                    <label>course</label>
                    <input type="text" value={addCourseFormData.course} readOnly />
                  </div>
                  <div className="form-field">
                    <label>course-end (จำนวนครั้ง)</label>
                    <select 
                      value={addCourseFormData['course-end']} 
                      onChange={(e) => setAddCourseFormData({...addCourseFormData, 'course-end': e.target.value})}
                    >
                      {lists.endDate.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>course-type</label>
                    <select 
                      value={addCourseFormData['course-type'] || 'class'} 
                      onChange={(e) => setAddCourseFormData({...addCourseFormData, 'course-type': e.target.value})}
                    >
                      <option value="class">class</option>
                      <option value="private">private</option>
                    </select>
                  </div>
                </div>

                <h4 style={{ margin: '20px 0 10px', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>เลือกวันเรียน</h4>
                <div className="day-selector-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                  {['จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์', 'อาทิตย์', 'จ-ศ'].map(day => {
                    const isActive = !!addCourseFormData.periods.find(p => p.day === day);
                    return (
                      <label key={day} className={`day-selector-item ${isActive ? 'active' : ''}`}>
                        <input 
                          type="checkbox" 
                          checked={isActive} 
                          onChange={() => handleDayToggle(day)} 
                          style={{ display: 'none' }}
                        />
                        {day}
                      </label>
                    );
                  })}
                </div>

                  <div className="selected-days-periods">
                    {addCourseFormData.periods.map((p) => (
                      <div key={p.day} className="selected-day-card">
                        <button 
                          type="button" 
                          className="close-card-btn" 
                          onClick={() => handleDayToggle(p.day)}
                          title="ลบวันเรียนนี้"
                        >
                          &times;
                        </button>
                        <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>{p.day}</p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                          <div className="form-field">
                            <label>เวลา</label>
                            <select value={p.time} onChange={(e) => handlePeriodChange(p.day, 'time', e.target.value)}>
                              {lists.time.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          </div>
                          <div className="form-field">
                            <label>ครูผู้สอน</label>
                            <select value={p.teacher} onChange={(e) => handlePeriodChange(p.day, 'teacher', e.target.value)}>
                              {lists.teacher.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setIsAddCourseModalOpen(false)}>ยกเลิก</button>
                <button type="submit" className="btn-primary" disabled={loading || addCourseFormData.periods.length === 0}>บันทึกข้อมูล</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {isAddSchoolCourseModalOpen && (
        <div className="modal-overlay" style={{ zIndex: 1200 }}>
          <div className="modal-content" style={{ width: '600px', maxWidth: '90%' }}>
            <button className="modal-close-btn" onClick={() => setIsAddSchoolCourseModalOpen(false)}>&times;</button>
            <h3 className="modal-header-title">เพิ่มข้อมูลคอร์ส</h3>
            <form onSubmit={handleAddSchoolCourseSubmit} className="modal-form-container">
              <div className="modal-form-body">
                <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                  <div className="form-field">
                    <label>course-ID</label>
                    <input type="text" value={addSchoolCourseFormData['school-course-ID']} readOnly />
                  </div>
                  <div className="form-field">
                    <label>course</label>
                    <select 
                      value={addSchoolCourseFormData.course} 
                      onChange={(e) => setAddSchoolCourseFormData({...addSchoolCourseFormData, course: e.target.value})}
                    >
                      <option value="จารุ - บรรจุหลักสูตร">จารุ - บรรจุหลักสูตร</option>
                      <option value="จารุ - วันเสาร์">จารุ - วันเสาร์</option>
                      <option value="ประสาทวุฒิ - E&I">ประสาทวุฒิ - E&I</option>
                      <option value="ประสาทวุฒิ - Beginner">ประสาทวุฒิ - Beginner</option>
                      <option value="สันติ - จินตคณิต">สันติ - จินตคณิต</option>
                      <option value="สันติ - ติวคณิต">สันติ - ติวคณิต</option>
                      <option value="สันติ - คณิตเพิ่ม">สันติ - คณิตเพิ่ม</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Course Start</label>
                    <input 
                      type="text" 
                      value={addSchoolCourseFormData['course-start'] || ''} 
                      onChange={(e) => {
                        let input = e.target.value;
                        if (e.nativeEvent.inputType === 'deleteContentBackward') {
                           setAddSchoolCourseFormData({...addSchoolCourseFormData, 'course-start': input});
                           return;
                        }
                        let digits = input.replace(/\D/g, '').substring(0, 6);
                        let formatted = '';
                        for(let i = 0; i < digits.length; i++) {
                           if(i === 2 || i === 4) formatted += '/';
                           formatted += digits[i];
                        }
                        setAddSchoolCourseFormData({...addSchoolCourseFormData, 'course-start': formatted});
                      }}
                      placeholder="วว/ดด/ปป (เช่น 01/11/66)"
                    />
                  </div>
                  <div className="form-field">
                    <label>course-end (จำนวนครั้ง)</label>
                    <select 
                      value={addSchoolCourseFormData['course-end']} 
                      onChange={(e) => setAddSchoolCourseFormData({...addSchoolCourseFormData, 'course-end': e.target.value})}
                    >
                      <option value="15 ครั้ง">15 ครั้ง</option>
                      <option value="16 ครั้ง">16 ครั้ง</option>
                      <option value="17 ครั้ง">17 ครั้ง</option>
                      <option value="18 ครั้ง">18 ครั้ง</option>
                      <option value="19 ครั้ง">19 ครั้ง</option>
                      <option value="20 ครั้ง">20 ครั้ง</option>
                    </select>
                  </div>
                </div>

                <h4 style={{ margin: '20px 0 10px', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>เลือกวันเรียน</h4>
                <div className="day-selector-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                  {['จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์', 'อาทิตย์', 'จ-ศ'].map(day => {
                    const isActive = !!addSchoolCourseFormData.periods.find(p => p.day === day);
                    return (
                      <label key={day} className={`day-selector-item ${isActive ? 'active' : ''}`}>
                        <input 
                          type="checkbox" 
                          checked={isActive} 
                          onChange={() => handleSchoolDayToggle(day)} 
                          style={{ display: 'none' }}
                        />
                        {day}
                      </label>
                    );
                  })}
                </div>

                <div className="selected-days-periods">
                  {addSchoolCourseFormData.periods.map((p) => (
                    <div key={p.day} className="selected-day-card">
                      <button 
                        type="button" 
                        className="close-card-btn" 
                        onClick={() => handleSchoolDayToggle(p.day)}
                        title="ลบวันเรียนนี้"
                      >
                        &times;
                      </button>
                      <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>{p.day}</p>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div className="form-field">
                          <label>เวลา</label>
                          <input 
                            type="text" 
                            value={p.time} 
                            placeholder="xx.xx-xx.xx"
                            onChange={(e) => {
                              let val = e.target.value;
                              if (e.nativeEvent.inputType === 'deleteContentBackward') {
                                handleSchoolPeriodChange(p.day, 'time', val);
                                return;
                              }
                              let digits = val.replace(/\D/g, '').substring(0, 8);
                              let formatted = '';
                              for (let i = 0; i < digits.length; i++) {
                                if (i === 2) formatted += '.';
                                if (i === 4) formatted += '-';
                                if (i === 6) formatted += '.';
                                formatted += digits[i];
                              }
                              handleSchoolPeriodChange(p.day, 'time', formatted);
                            }}
                          />
                        </div>
                        <div className="form-field">
                          <label>ครูผู้สอน</label>
                          <select value={p.teacher} onChange={(e) => handleSchoolPeriodChange(p.day, 'teacher', e.target.value)}>
                            {lists.teacher.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="form-field" style={{ marginTop: '10px' }}>
                         <label>ระดับชั้น</label>
                         <input 
                            type="text" 
                            value={p.level || ''} 
                            onChange={(e) => handleSchoolPeriodChange(p.day, 'level', e.target.value)}
                            placeholder="เช่น ป.1 หรือ ม.3"
                         />
                      </div>
                    </div>
                  ))}
                </div>

              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setIsAddSchoolCourseModalOpen(false)}>ยกเลิก</button>
                <button type="submit" className="btn-primary" disabled={loading || addSchoolCourseFormData.periods.length === 0}>บันทึกข้อมูล</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isSchoolTimetableModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content modal-lg">
            <button className="modal-close-btn" onClick={() => setIsSchoolTimetableModalOpen(false)}>&times;</button>
            <h3 className="modal-header-title">ข้อมูลจัดการตารางสอนโรงเรียน (school-course-ID)</h3>
            
            <div className="transfer-context-header" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '15px', padding: '10px' }}>
              <button className="btn-primary" onClick={openAddSchoolCourseModal}>+ เพิ่มข้อมูล</button>
            </div>

            <div className="table-container transfer-table-container">
              {isSchoolTimetableLoading ? <div className="loading-indicator">กำลังโหลดข้อมูล...</div> : (
                <table>
                  <thead>
                    <tr>
                      {schoolTimetableKeys.length > 0 ? (
                        <>
                          {schoolTimetableKeys.map(key => <th key={key}>{key}</th>)}
                          <th style={{ textAlign: 'center' }}>จัดการ</th>
                        </>
                      ) : <th>ไม่มีข้อมูล</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {schoolTimetableList.length > 0 ? (
                      schoolTimetableList.map((course) => {
                        return (
                          <tr key={course.id}>
                            {schoolTimetableKeys.map(key => {
                              if (key === 'day' || key === 'teacher' || key === 'time' || key === 'level') {
                                const stackedValues = [];
                                if (course.period && typeof course.period === 'object') {
                                  if (Array.isArray(course.period)) {
                                    course.period.forEach(p => stackedValues.push(p[key] || '-'));
                                  } else {
                                    let i = 1;
                                    while (course.period[`day-${i}`] !== undefined || course.period[`teacher-${i}`] !== undefined || course.period[`time-${i}`] !== undefined || course.period[`level-${i}`] !== undefined) {
                                      stackedValues.push(course.period[`${key}-${i}`] || '-');
                                      i++;
                                    }
                                  }
                                } else {
                                  stackedValues.push(course[key] || course[`${key}-1`] || '-');
                                }
                                
                                return (
                                  <td key={key}>
                                    {stackedValues.length > 0 ? stackedValues.map((val, idx) => (
                                      <div key={idx} style={{ padding: '2px 0', borderBottom: idx < stackedValues.length - 1 ? '1px solid #eee' : 'none', minHeight: '24px' }}>
                                        {val}
                                      </div>
                                    )) : '-'}
                                  </td>
                                );
                              }

                              const val = course[key];
                              return <td key={key}>{val !== undefined && val !== null && val !== '' ? String(val) : '-'}</td>;
                            })}
                            <td style={{ textAlign: 'center', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                               <button className="icon-btn edit-btn" style={{ marginRight: '8px' }} onClick={() => handleEditSchoolCourse(course)} title="แก้ไข">
                                  <EditIcon />
                               </button>
                               <button className="icon-btn btn-transfer-icon" onClick={() => handleSelectTransferCourse(course)} title="เลือกคอร์สเพื่อ Transfer">
                                  <SwapHorizIcon />
                               </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr><td colSpan={schoolTimetableKeys.length + 1}>ไม่มีข้อมูลจาก collection school-course-ID</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close-btn" onClick={() => setIsModalOpen(false)}>&times;</button>
            <h3 className="modal-header-title">{isEditing ? 'แก้ไขข้อมูล' : 'เพิ่มข้อมูลใหม่'}</h3>
            <form onSubmit={handleSubmit} className="modal-form-container">
              <div className="modal-form-body">
                {receiptType === 'office' ? (
                  <>
                    <h4 className="form-section-header">ส่วนข้อมูลนักเรียน</h4>
                    <div className="form-grid">{config.formFields.filter(f => f.section === 'student').map(renderFormField)}</div>
                    <h4 className="form-section-header">ส่วนข้อมูลลงทะเบียน</h4>
                    <div className="form-grid">{config.formFields.filter(f => f.section === 'registration').map(renderFormField)}</div>
                  </>
                ) : (
                  <div className="form-grid">{config.formFields.map(renderFormField)}</div>
                )}
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn-primary" disabled={loading}>{isEditing ? 'บันทึก' : 'สร้าง'}</button>
                <button type="button" className="btn-secondary" onClick={() => setIsModalOpen(false)}>ยกเลิก</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <PrintModal isOpen={isPrintModalOpen}>
        {receiptToPrint && receiptType === 'office' && ( <OfficeReceiptPrint receiptData={receiptToPrint} /> )}
        {receiptToPrint && receiptType === 'school' && ( <SchoolReceiptPrint receiptData={receiptToPrint} /> )}
      </PrintModal>

      <div className="main-header">
        <Link to="/main">
            <img src="/Wisdom-Logo.png" alt="Logo" className="header-logo" />
        </Link>
        <div className="header-title">
          <select className="receipt-type-selector" value={receiptType} onChange={handleTypeChange}>
            <option value="office">Office Receipt</option>
            <option value="school">School Receipt</option>
          </select>

          <select
            className="receipt-type-selector"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            disabled={!availableYears.length}
          >
            {availableYears.length > 0 ? (
              <>
                <option value="All">All</option>
                {availableYears.map(year => (
                  <option key={year} value={year}>{`ปี ${year}`}</option>
                ))}
              </>
            ) : (
              <option>Loading years...</option>
            )}
          </select>

        </div>
        <div className="header-actions">
          {receiptType === 'school' && (
            <button 
              className="btn-primary" 
              style={{ marginRight: '10px', backgroundColor: '#10b981', width: '200px' }} 
              onClick={handleOpenSchoolTimetable}
            >
              + เพิ่มตารางสอนโรงเรียน
             </button>
          )}
          <button className="btn-primary" style={{ width: '200px' }} onClick={openCreateModal} disabled={loading || !selectedYear || selectedYear === 'All'}>+ เพิ่มข้อมูลใหม่</button>
          <input type="text" placeholder={config.searchPlaceholder} value={filterQuery} onChange={(e) => setFilterQuery(e.target.value)} className="filter-input" />
        </div>
      </div>
      {loading && <div className="loading-indicator">Loading...</div>}
      <div className={`table-container ${receiptType}-receipt-table`}>
        <table>
          <thead>
            <tr>
              {config.tableColumns.map(c => (<th key={c} className={config.sortableColumns.includes(c) ? 'sortable' : ''} onClick={() => config.sortableColumns.includes(c) && requestSort(c)}>{c}{sortConfig.key === c && <span className="sort-icon">{sortConfig.direction === 'ascending' ? ' ▲' : ' ▼'}</span>}</th>))}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedData.map((item) => (
              <tr key={item.rowId}>
                {config.tableColumns.map(c => (
                  <td key={c} data-label={c}>
                    {
                      (c === 'วันที่' || c === 'วันเริ่มคอร์ส')
                        ? formatDateForDisplay(item[c])
                        : (c === 'Completed' ? item['complete'] 
                        : (c === 'Course' ? (item['Couse'] || item['course'] || item['Course']) 
                        : (c === 'หลักสูตร' ? (item['หลักสูตร'] || item['course']) 
                        : (c === 'วิธีการชำระ' ? (item['วิธีการชำระ'] || item['Payment-method']) 
                        : (c === 'ชื่อ-สกุล' ? `${item['ชื่อ']||''} ${item['นามสกุล']||''}` 
                        : (c === 'จำนวนเงิน' ? (Number(item[c]) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : (c === 'ราคาคอร์ส' ? (parseFloat(String(item['รวม'] || item['course-price'] || '0').replace(/,/g, '')) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : (c === 'ส่วนลด' ? (parseFloat(String(item['หักส่วนลด'] || item['discount'] || '0').replace(/,/g, '')) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : item[c]))))))))
                    }
                  </td>
                ))}
                <td className="actions-cell">
                  <button className="btn-icon btn-edit-icon" onClick={() => openEditModal(item)} disabled={loading} title="Edit">
                    <EditIcon style={{ fontSize: 28 }} />
                  </button>
                  <button className="btn-icon btn-delete-icon" onClick={() => handleDelete(item.rowId)} disabled={loading} title="Delete">
                    <DeleteIcon style={{ fontSize: 28 }} />
                  </button>
                  <button className="btn-icon btn-print-icon" onClick={() => handlePrint(item)} disabled={loading} title="Print">
                    <PrintIcon style={{ fontSize: 28 }} />
                  </button>
                  {receiptType === 'office' && (
                    <button className="btn-icon btn-transfer-icon" onClick={() => handleTransfer(item)} disabled={loading || item['complete'] === 'Yes'} title="Transfer">
                      <SwapHorizIcon style={{ fontSize: 28 }} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ReceiptApp;